import React, { createContext, useContext, useEffect, useMemo, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { useAppMode } from './AppModeContext';
import { MOCK_USER } from '../data/mockData';

// Task 4.5: split out of CanvasDataContext.tsx. This context owns the
// active-user/available-users/switching surface: who is the currently
// active Canvas identity, which users can be switched to, and the derived
// `userId` the rest of the app keys its data off of.
//
// This deliberately depends on AppModeContext (for the active mode/
// guestCredentials -- `userId` differs by mode for the same underlying
// person) rather than the other way around, so AppModeProvider must be an
// ancestor of CanvasSessionProvider. See AppModeContext.tsx's file comment
// for why that direction was chosen.
//
// The higher-level "switch to this user AND reset whatever's cached for the
// old one" orchestration (selectCanvasUser / selectGuestCanvasUser /
// switchGuestUser in the pre-4.5 file) stayed in CanvasDataContext instead
// of moving here: it needs to clear CanvasDataContext's own per-course
// caches, which would otherwise create a dependency cycle (this context
// would need something CanvasDataContext owns, while CanvasDataContext
// already needs userId/availableUsers from this context). What's left here
// is the passive identity data those orchestration functions read.

interface CanvasSessionContextType {
  currentUser: any;
  availableUsers: any[];
  switchCanvasUser: (userId: string | number) => Promise<boolean>;
  userId: string | null;
}

const CanvasSessionContext = createContext<CanvasSessionContextType | undefined>(undefined);

export const useCanvasSession = (): CanvasSessionContextType => {
  const context = useContext(CanvasSessionContext);
  if (!context) {
    throw new Error('useCanvasSession must be used within a CanvasSessionProvider');
  }
  return context;
};

interface CanvasSessionProviderProps {
  children: ReactNode;
}

export const CanvasSessionProvider: React.FC<CanvasSessionProviderProps> = ({ children }) => {
  const { currentUser, availableUsers, switchCanvasUser } = useAuth();
  const { coursesMode, guestCredentials, offlineCanvasUserId } = useAppMode();

  const userId = useMemo(() => {
    if (coursesMode === 'demo') {
      return MOCK_USER.id.toString();
    }
    if (coursesMode === 'guest' && guestCredentials?.userId) {
      return guestCredentials.userId;
    }
    // Task 6.0t. Before this branch, offline mode fell through to the
    // parent's stored default child below -- so viewing Audrey offline gave
    // the app Isaac's id. Three components (6.0p, 6.0q, 6.0s) each patched
    // that locally; this is the one place it should be decided.
    if (coursesMode === 'offline') {
      return offlineCanvasUserId;
    }
    if (currentUser?.canvasUserId) {
      return currentUser.canvasUserId;
    }
    return null;
  }, [coursesMode, guestCredentials, offlineCanvasUserId, currentUser]);

  // Task 5.2c: ASSERT the string invariant here; deliberately do NOT
  // re-normalise it.
  //
  // `userId` is a query-key segment (useCourseData.ts's coursesQueryKey,
  // useChildrenCourseData.ts's per-child key), and ['courses', 'auth', 111]
  // and ['courses', 'auth', '111'] are two different cache entries. So the
  // value has to have exactly one spelling -- but the place to give it one
  // is where it enters auth state (AuthContext's toCanvasUserId), not here.
  //
  // Re-normalising here would have been a fix in name only. This context is
  // not the only reader of currentUser.canvasUserId: AppModeContext's
  // getActiveCredentials() reads it directly, and THAT value is what
  // useCourseData builds its courseDetails/courseHistory keys from and what
  // clearCache() removes by. A String() in this file would have left that
  // path numeric -- so the bug would have survived in exactly the place (a
  // cache clear on user switch) that made it worth fixing, while looking
  // fixed from here.
  //
  // An assertion instead keeps ownership of the invariant unambiguous and
  // turns a future regression into a visible message rather than a silent
  // second cache entry. It is dev-only: in production a duplicate cache
  // entry is a wasted fetch, not something worth a console error on every
  // identity change.
  useEffect(() => {
    if (import.meta.env.DEV && userId !== null && typeof userId !== 'string') {
      console.error(
        `CanvasSessionContext: userId must be a string, got ${typeof userId} (${String(userId)}). ` +
          'It is used as a query-key segment -- normalise it at its source in AuthContext, not here.'
      );
    }
  }, [userId]);

  const value = useMemo(() => ({
    currentUser,
    availableUsers,
    switchCanvasUser,
    userId,
  }), [currentUser, availableUsers, switchCanvasUser, userId]);

  return <CanvasSessionContext.Provider value={value}>{children}</CanvasSessionContext.Provider>;
};

export default CanvasSessionContext;
